<template>
    <div
        v-if="item.authorised"
        :class="{
            'app-list__item': true,
            'app-list__item--active': item.$active,
            'app-list__item--disabled': item.$disabled,
            'app-list__item--expanded': item.$expanded,
            'app-list__item--hidden': item.$hidden,
            'app-list__item--loading': item.$loading,
            'app-list__item--readonly': item.$readonly,
            'app-list__item--clickable': item.is_clickable
        }"
    >
        <div v-if="!item.$hidden" class="app-list__item__wrap" role="button" @click="onItemClick" @keyup="onItemClick">
            <div v-if="item.icon" class="app-list__item__prepend">
                <v-icon color="primaryBackground" size="1.2rem">
                    {{ item.icon }}
                </v-icon>
            </div>
            <div class="app-list__item__name">
                <app-tooltip :value="item.tooltip" :disabled="!item.tooltip" class="app-list__item__name__tooltip">
                    <span class="app-list__item__name__text">{{ item.name }}</span>
                </app-tooltip>
            </div>
            <div class="app-list__item__append">
                <v-progress-circular
                    v-if="item.$loading"
                    :size="16"
                    :width="2"
                    color="primaryBackground"
                    indeterminate
                />
                <app-button
                    v-else-if="item.is_parent"
                    ref="expandButton"
                    class="app-list__item__expand"
                    icon="expand"
                    icon-size="1rem"
                    small
                    :disabled="item.$disabled"
                    @click.stop="onItemExpand"
                />
            </div>
        </div>

        <v-slide-y-transition v-if="item.is_parent">
            <div v-show="item.$expanded" class="app-list__item__list">
                <app-list-item
                    v-for="(subitem, subindex) in item.items"
                    :key="subitem.id"
                    v-model="item.items[`${subindex}`]"
                    :items="items"
                />
            </div>
        </v-slide-y-transition>
    </div>
</template>

<script>
import AppListItem from '@/models/AppListItem';

export default {
    name: 'AppListItem',
    props: {
        value: {
            type: AppListItem,
            required: true
        },
        items: {
            type: Array,
            required: true
        }
    },
    computed: {
        item: {
            get() {
                return this.value;
            },
            set(item) {
                this.$emit('input', item);
            }
        }
    },
    methods: {
        async setup() {
            this.item = await this.item.setup();
        },
        onItemClick() {
            if (!this.item.is_clickable) {
                return;
            }

            for (let item of this.items) {
                item.set_inactive();
            }

            this.item.$active = true;
            this.go(this.item.route);
        },
        async onItemExpand() {
            this.item.$expanded = !this.item.$expanded;

            if (this.item.$expanded && !this.item.items.length) {
                this.item = await this.item.load();
            }
        }
    }
};
</script>

<style lang="scss">
$appListItemHeight: 2.4rem;
$appListSubitemHeight: 2.1rem;

.app-list {
    display: block;
    padding: 0;

    &__item {
        font-size: 1rem;
        user-select: none;
        color: var(--v-primaryText-base);

        &__wrap {
            position: relative;
            display: flex;
            border-radius: 4px 0 0 4px;
            height: $appListItemHeight;
            color: inherit;
            padding: 0 1rem;

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 100%;
                background: currentColor;
                opacity: 0;
                transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
                pointer-events: none;
                border-radius: 4px 0 0 4px;
            }
        }

        &__prepend {
            flex: 0 0 auto;
            width: 2.4rem;
            height: $appListItemHeight;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            padding-right: 0.5rem;
            > i {
                height: 100%;
                color: inherit;
            }
        }

        &__name {
            flex: 1 1 auto;
            height: $appListItemHeight;
            width: calc(100% - 3rem);
            font-size: 0.95rem;
            display: flex;
            align-items: center;
            z-index: 1;
            &__tooltip {
                width: 100%;
            }
            &__text {
                display: block;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }
        }

        &__append {
            flex: 0 0 auto;
            margin-left: 0.2rem;
            width: 3rem;
            height: $appListItemHeight;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: end;
            z-index: 1;
            .v-progress-circular {
                margin: 6px;
            }
        }

        &__expand {
            i {
                height: 100%;
                width: 100%;
                transform: rotate(0deg);
            }
        }

        &__list {
            padding-left: 1.4rem;
            .app-list__item {
                &__wrap,
                &__prepend,
                &__name,
                &__append {
                    height: $appListSubitemHeight;
                }
                &__wrap {
                    padding-left: 0.7rem;
                }
                &__prepend {
                    width: 1.8rem;
                    padding-right: 0.3rem;
                }
                &__prepend > i {
                    font-size: 1rem !important;
                }
                &__name {
                    font-size: 0.9rem;
                }
            }
        }

        &--active {
            > .app-list__item__wrap {
                box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.1);
            }

            > .app-list__item__wrap:before {
                opacity: 1;
                background: var(--v-background-base);
            }
        }
        &--disabled {
            color: rgba(0, 0, 0, 0.5);
            > .app-list__item__wrap {
                cursor: default;
            }
            > .app-list__item__wrap:before {
                opacity: 0;
            }
            > .app-list__item__wrap > .app-list__item__prepend i,
            > .app-list__item__wrap > .app-list__item__append i {
                color: rgba(0, 0, 0, 0.38);
            }
        }
        &--expanded {
            > .app-list__item__wrap > .app-list__item__append > .app-list__item__expand i {
                transform: rotate(90deg);
            }
        }
        &--hidden {
            display: none;
        }
        &--readonly {
            > .app-list__item__wrap {
                cursor: default;
            }
            > .app-list__item__wrap:before {
                opacity: 0;
            }
        }
        &--clickable {
            > .app-list__item__wrap {
                cursor: pointer;
            }
            > .app-list__item__wrap:hover:before {
                opacity: 0.1;
            }
        }
    }
}
</style>
